import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, Target, Star, TrendingUp, Calendar, ArrowRight } from "lucide-react";
import DeadlineBanner from "@/components/DeadlineBanner";

function StatCard({ label, value, icon: Icon, accent, testId }) {
  return (
    <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-[0.15em] text-[#AAB7C7]">{label}</span>
        <Icon className={`w-4 h-4 ${accent || "text-[#C7F500]"}`} />
      </div>
      <div className="font-heading text-3xl font-bold">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [comps, setComps] = useState([]);
  const [selectedComp, setSelectedComp] = useState(null);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/competitions");
      setComps(data);
      if (data.length) setSelectedComp(data[0]);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!selectedComp) return;
    setStatsLoading(true);
    (async () => {
      const [s, lb, fx, mp] = await Promise.all([
        api.get(`/me/stats?competition_id=${selectedComp.id}`),
        api.get(`/leaderboards/${selectedComp.id}/season`),
        api.get(`/competitions/${selectedComp.id}/fixtures`),
        api.get(`/predictions/mine?competition_id=${selectedComp.id}`),
      ]);
      setStats(s.data);
      setLeaderboard(lb.data);
      setFixtures(fx.data);
      setPredictions(mp.data);
      setStatsLoading(false);
    })();
  }, [selectedComp]);

  if (loading) return <div className="text-[#AAB7C7]" data-testid="dashboard-loading">Loading…</div>;

  if (!comps.length) {
    return (
      <div className="text-center py-16 fade-up" data-testid="empty-dashboard">
        <Trophy className="w-12 h-12 mx-auto text-[#C7F500] mb-3" />
        <h2 className="font-heading text-2xl font-bold mb-2">Welcome, {user?.name}!</h2>
        <p className="text-[#AAB7C7] mb-6">You're not in any competitions yet.</p>
        <Link to="/app/competitions" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#C7F500] hover:bg-[#D9FF27] font-semibold" data-testid="cta-join-competition">
          Join or create a competition <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const upcoming = fixtures.filter((f) => f.status !== "complete").slice(0, 5);
  const recent = fixtures.filter((f) => f.status === "complete").slice(-5).reverse();
  const predByFixture = Object.fromEntries(predictions.map((p) => [p.fixture_id, p]));
  const myRow = leaderboard.find((r) => r.user_id === user?.id);
  const jokerOn = selectedComp?.joker_enabled !== false;
  const joker = predictions.find((p) => p.is_joker);
  const jokerFixture = joker ? fixtures.find((f) => f.id === joker.fixture_id) : null;

  return (
    <div className="fade-up space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C7F500] font-semibold">Welcome back, {user?.name?.split(" ")[0]}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-1">Your Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#AAB7C7]">Competition</label>
          <select
            value={selectedComp?.id}
            onChange={(e) => setSelectedComp(comps.find((c) => c.id === e.target.value))}
            data-testid="dashboard-competition-select"
            className="bg-[#0D2038] border border-[#1F3A5C] rounded-lg px-3 py-2 text-sm"
          >
            {comps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <DeadlineBanner competitionId={selectedComp?.id} />

      {/* Stat cards */}
      {statsLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-stats-loading">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C] animate-pulse h-[100px]" />
          ))}
        </div>
      ) : (
        <div className={`grid grid-cols-2 gap-3 ${jokerOn ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          <StatCard testId="stat-position" label="Position" value={myRow?.position ? `#${myRow.position}` : "—"} icon={Trophy} />
          <StatCard testId="stat-points" label="Total Points" value={stats?.total_points ?? 0} icon={Target} />
          <StatCard testId="stat-exact" label="Exact Scores" value={stats?.exact_correct ?? 0} icon={TrendingUp} />
          {jokerOn && <StatCard testId="stat-joker-rate" label="Joker Hit Rate" value={`${stats?.joker_rate ?? 0}%`} icon={Star} accent="text-[#F5B942]" />}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Upcoming fixtures + predict shortcut */}
        <div className="lg:col-span-2 p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#C7F500]" />
              <h2 className="font-heading text-lg font-semibold">Upcoming fixtures</h2>
            </div>
            <Link to={`/app/competitions/${selectedComp?.id}`} className="text-xs text-[#C7F500] hover:underline" data-testid="goto-predict-link">Predict →</Link>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-sm text-[#AAB7C7]">No upcoming fixtures yet.</p>
          ) : (
            <div className="space-y-2">
              {upcoming.map((f) => {
                const p = predByFixture[f.id];
                return (
                  <div key={f.id} className={`flex items-center justify-between p-3 rounded-xl bg-[#071426] border ${p?.is_joker ? "border-[#F5B942]/40 joker-glow" : "border-[#1F3A5C]"}`}>
                    <div className="flex-1 text-right font-medium pr-3 truncate">{f.home_team}</div>
                    <div className="flex items-center gap-2 px-2">
                      {p ? (
                        <span className="font-heading text-lg font-bold">{p.home_goals}-{p.away_goals}</span>
                      ) : (
                        <span className="text-xs text-[#AAB7C7]">vs</span>
                      )}
                      {p?.is_joker && <Star className="w-4 h-4 text-[#F5B942]" />}
                    </div>
                    <div className="flex-1 pl-3 font-medium truncate">{f.away_team}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Joker + leaderboard snapshot */}
        <div className="space-y-5">
          {jokerOn && (
          <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#F5B942]/30" data-testid="dashboard-joker-card">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-5 h-5 text-[#F5B942]" />
              <h3 className="font-heading text-base font-semibold">This week's Joker</h3>
            </div>
            {jokerFixture ? (
              <div className="text-sm">
                <div className="font-medium">{jokerFixture.home_team} <span className="text-[#AAB7C7]">vs</span> {jokerFixture.away_team}</div>
                <div className="text-xs text-[#AAB7C7] mt-1">Your prediction: {joker.home_goals}-{joker.away_goals}</div>
              </div>
            ) : (
              <p className="text-sm text-[#AAB7C7]">Pick a Joker this gameweek to double a fixture's points.</p>
            )}
          </div>
          )}

          <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#C7F500]" />
                <h3 className="font-heading text-base font-semibold">Leaderboard</h3>
              </div>
              <Link to={`/app/competitions/${selectedComp?.id}?tab=leaderboards`} className="text-xs text-[#C7F500] hover:underline" data-testid="goto-leaderboards-link">View all →</Link>
            </div>
            <div className="space-y-1">
              {leaderboard.slice(0, 5).map((r) => (
                <div key={r.user_id} className={`flex items-center justify-between text-sm py-1.5 px-2 rounded-lg ${r.user_id === user?.id ? "bg-[#132A45] border-l-2 border-[#C7F500]" : ""}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-6 text-center text-[#AAB7C7] font-mono text-xs">{r.position}</span>
                    <span className="truncate">{r.name}</span>
                  </div>
                  <span className="font-heading font-bold">{r.total_points}</span>
                </div>
              ))}
              {leaderboard.length === 0 && <p className="text-xs text-[#AAB7C7]">No players yet.</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Recent results */}
      {recent.length > 0 && (
        <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
          <h2 className="font-heading text-lg font-semibold mb-4">Recent results</h2>
          <div className="space-y-2">
            {recent.map((f) => {
              const p = predByFixture[f.id];
              return (
                <div key={f.id} className="flex items-center justify-between p-3 rounded-xl bg-[#071426] border border-[#1F3A5C]">
                  <div className="flex-1 text-right pr-3 truncate">{f.home_team}</div>
                  <div className="flex items-center gap-3 px-3">
                    <span className="font-heading text-lg font-bold text-[#C7F500]">{f.home_score}-{f.away_score}</span>
                    {p && (
                      <div className="text-xs text-[#AAB7C7]">
                        you: {p.home_goals}-{p.away_goals}
                        <span className={`ml-2 font-bold ${p.points_awarded > 0 ? "text-[#C7F500]" : "text-red-400"}`}>+{p.points_awarded}</span>
                        {p.is_joker && <Star className="inline w-3 h-3 ml-1 text-[#F5B942]" />}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 pl-3 truncate">{f.away_team}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
